'use client'
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react'
import Image from 'next/image'
import { useEffect, useState } from 'react'

import { SolutionBySlugDTO } from '@/lib/models/model-types/solution/solution.types'
import { Markdown } from '@/lib/ui-kit/components/Markdown.component'
import { ButtonIcon } from '@/lib/ui-kit/components/ui/ButtonIcon.component'
import { HeroTitle } from '@/lib/ui-kit/components/ui/HeroTitle.component'
import { IconType } from '@/lib/ui-kit/components/ui/Icon.types'

const CarouselPrevious = ({
  emblaApi,
}: {
  emblaApi: UseEmblaCarouselType[1]
}) => {
  const isDisabled = !emblaApi?.canScrollPrev()
  return (
    <ButtonIcon
      className="absolute left-[-5rem] top-1/2 hidden -translate-y-1/2 transform xl:flex xl:items-center xl:justify-center"
      isDisabled={isDisabled}
      onClick={() => emblaApi?.scrollPrev()}
      iconType={IconType.arrow_left}
    />
  )
}

const CarouselNext = ({ emblaApi }: { emblaApi: UseEmblaCarouselType[1] }) => {
  const isDisabled = !emblaApi?.canScrollNext()
  return (
    <ButtonIcon
      className="absolute right-[-5rem] top-1/2 hidden -translate-y-1/2 transform xl:flex xl:items-center xl:justify-center"
      isDisabled={isDisabled}
      onClick={() => emblaApi?.scrollNext()}
      iconType={IconType.arrow_right}
    />
  )
}

export const SectionCoreComponents = ({
  data,
  apiAssetsUrl,
}: {
  data: SolutionBySlugDTO
  apiAssetsUrl: string | undefined
}) => {
  const {
    coreComponentsHeading,
    coreComponentsSubheading,
    coreComponentsCard,
  } = data
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    containScroll: 'trimSnaps',
  })
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = () => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }

  useEffect(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('select', onSelect)
    onSelect()
  }, [emblaApi])

  return (
    <section className="m-auto max-w-[1440px] pb-6 pt-10 xl:pb-12 xl:pt-20">
      <div className="px-6 xl:px-10">
        {coreComponentsHeading && (
          <Markdown
            className="heading-xl pb-6 xl:pb-10"
            components={{ p: HeroTitle }}
          >
            {coreComponentsHeading}
          </Markdown>
        )}
        <div className="grid grid-cols-1 xl:grid-cols-6">
          <div className="col-span-1 xl:col-span-2">
            {coreComponentsSubheading && (
              <div className="paragraph-xxl">
                <Markdown components={{ p: 'p' }}>
                  {coreComponentsSubheading}
                </Markdown>
              </div>
            )}
          </div>
        </div>
        {coreComponentsCard && coreComponentsCard.length > 0 && (
          <>
            {/* Carousel for mobile */}
            <div className="relative mx-auto mt-8 w-full xl:hidden">
              <div className="embla" ref={emblaRef}>
                <div className="embla__container flex">
                  {coreComponentsCard.map((card, index) => (
                    <div
                      className="embla__slide border-gray-300 relative gap-5 px-2.5"
                      key={index}
                    >
                      <div className="embla__slide flex h-[23.75rem] flex-col justify-between bg-white p-10">
                        <div className="relative">
                          <div className="">
                            <Image
                              src={`${apiAssetsUrl || ''}${card.icon.url}`}
                              alt={
                                card.icon.alternativeText ||
                                'Core components Image'
                              }
                              width="200"
                              height="200"
                              className="h-[100px] w-[100px]"
                            />
                          </div>
                        </div>
                        <div>
                          <h3 className="paragraph-xxl mb-6 font-medium">
                            {card.title}
                          </h3>
                          <p className="paragraph-s text-gray-500">
                            {card.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-300 mt-2 w-full bg-gray-400">
                <div className="flex justify-center">
                  {scrollSnaps.map((_, index) => (
                    <div
                      key={index}
                      className={`mx-1 h-1 w-[50%] duration-300 ${
                        index === selectedIndex ? 'bg-black' : 'bg-transparent'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <CarouselPrevious emblaApi={emblaApi} />
              <CarouselNext emblaApi={emblaApi} />
            </div>

            {/* Grid for desktop */}
            <div className="mt-8 hidden gap-5 xl:grid xl:grid-cols-3">
              {coreComponentsCard.map((card, index) => (
                <div
                  key={index}
                  className="flex h-[23.75rem] flex-col justify-between bg-white p-10"
                >
                  <div className="relative">
                    <div className="">
                      <Image
                        src={`${apiAssetsUrl || ''}${card.icon.url}`}
                        alt={
                          card.icon.alternativeText || 'Core components Image'
                        }
                        width="200"
                        height="200"
                        className="h-[100px] w-[100px]"
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="paragraph-xxl mb-6 font-medium">
                      {card.title}
                    </h3>
                    <p className="paragraph-s text-gray-500">
                      {card.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  )
}
